import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type PriorityLevel = 'niedrig' | 'mittel' | 'hoch' | 'kritisch';

const LEVEL_INDEX: Record<PriorityLevel, number> = {
  niedrig: 1,
  mittel: 2,
  hoch: 3,
  kritisch: 4,
};

@Component({
  selector: 'app-priority-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="priority-pill" [attr.data-level]="level()" data-testid="priority-pill" [attr.title]="title() || null">
      <span class="priority-pill__bars" aria-hidden="true">
        @for (i of bars; track i) {
          <span
            class="priority-pill__bar"
            [class.priority-pill__bar--active]="i <= index()"
            [attr.data-index]="i"
          ></span>
        }
      </span>
      <span class="priority-pill__label">{{ label() }}</span>
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .priority-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .priority-pill__bars {
      display: inline-flex;
      align-items: flex-end;
      gap: 0.125rem;
      height: 0.875rem;
    }

    .priority-pill__bar {
      width: 0.1875rem;
      border-radius: 0.0625rem;
      background: var(--to-border-soft);
    }

    .priority-pill__bar[data-index='1'] {
      height: 0.375rem;
    }
    .priority-pill__bar[data-index='2'] {
      height: 0.5625rem;
    }
    .priority-pill__bar[data-index='3'] {
      height: 0.75rem;
    }
    .priority-pill__bar[data-index='4'] {
      height: 0.875rem;
    }

    .priority-pill[data-level='niedrig'] .priority-pill__bar--active {
      background: var(--to-slate-dot);
    }
    .priority-pill[data-level='mittel'] .priority-pill__bar--active {
      background: var(--to-warn-border);
    }
    .priority-pill[data-level='hoch'] .priority-pill__bar--active {
      background: var(--to-orange-dot);
    }
    .priority-pill[data-level='kritisch'] .priority-pill__bar--active {
      background: var(--to-red-dot);
    }

    .priority-pill__label {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--to-text-muted);
    }
    .priority-pill[data-level='mittel'] .priority-pill__label {
      /* Bundle: page-anforderung.jsx:125 setzt "Mittel" auf THEME.text, nicht textMuted (NEX-1085 Review W5). */
      color: var(--to-text);
    }
    .priority-pill[data-level='hoch'] .priority-pill__label,
    .priority-pill[data-level='kritisch'] .priority-pill__label {
      font-weight: 700;
    }
    .priority-pill[data-level='hoch'] .priority-pill__label {
      color: var(--to-brand-deep);
    }
    .priority-pill[data-level='kritisch'] .priority-pill__label {
      color: var(--to-red-fg);
    }
  `,
})
export class PriorityPillComponent {
  readonly level = input.required<PriorityLevel>();
  readonly label = input.required<string>();
  // Voller Tooltip-Text (inkl. Praefix wie "Priorität: Hoch"). Der Baustein
  // uebersetzt nichts selbst — der Aufrufer reicht den fertig lokalisierten
  // Text durch (NEX-1085 Review, sgraetz C1).
  readonly title = input<string>('');
  readonly bars = [1, 2, 3, 4];
  readonly index = computed(() => LEVEL_INDEX[this.level()]);
}
